import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, Loader2, Mail, Globe, Shield, Bell, Settings, Server, Lock, Clock, Upload, HardDrive } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Toggle = ({ checked, onChange, color = 'cyan' }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only peer" />
    <div className={`w-11 h-6 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${color === 'red' ? 'peer-checked:bg-red-500' : 'peer-checked:bg-emerald-500'}`} />
  </label>
);

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState({
    site_name: '', site_description: '', contact_email: '',
    enable_notifications: true, enable_public_registration: true,
    maintenance_mode: false, max_upload_size: 10, session_timeout: 30,
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
    smtp_from_email: '', smtp_from_name: '', smtp_encryption: 'tls',
    app_version: '1.0.0', app_icon_url: '', pwa_install_count: 0, changelog: [],
  });
  const [newLog, setNewLog] = useState({ version: '', description: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/settings`, { headers });
      setS(prev => ({ ...prev, ...res.data }));
    } catch { console.error('Error loading settings'); }
    setLoading(false);
  };

  const set = (k, v) => setS(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/settings`, s, { headers });
      setS(prev => ({ ...prev, ...res.data }));
      toast.success('Paramètres sauvegardés');
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const addChangelog = () => {
    if (!newLog.version || !newLog.description) return;
    const logs = [...(s.changelog || []), { ...newLog, date: new Date().toISOString() }];
    setS(p => ({ ...p, changelog: logs }));
    setNewLog({ version: '', description: '' });
  };

  const removeChangelog = (i) => {
    const logs = [...(s.changelog || [])];
    logs.splice(i, 1);
    setS(p => ({ ...p, changelog: logs }));
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/api/upload/cms-image`, fd, { headers });
      set('app_icon_url', res.data.url);
      toast.success('Icône uploadée');
    } catch { toast.error('Erreur upload'); }
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-gray-400" size={24}/></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-6" data-testid="settings-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center shadow-sm"><Settings className="text-gray-600" size={18}/></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Paramètres</h1>
              <p className="text-xs text-gray-400">Configuration globale de la plateforme</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="save-settings-btn">
            {saving ? <Loader2 size={16} className="animate-spin mr-2"/> : <Save size={16} className="mr-2"/>} Enregistrer
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* General */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5"><Globe className="text-[#35AFE4]" size={20}/><h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">General</h2></div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Nom du site</label>
                <input value={s.site_name} onChange={e => set('site_name', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="setting-site-name"/>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Description</label>
                <textarea value={s.site_description} onChange={e => set('site_description', e.target.value)} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm resize-none"/>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1"><Mail className="inline mr-1" size={14}/>Email de contact</label>
                <input value={s.contact_email} onChange={e => set('contact_email', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="setting-email"/>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5"><Shield className="text-violet-600" size={20}/><h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Sécurité & Accès</h2></div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div><p className="font-semibold text-gray-900 text-sm">Inscriptions publiques</p><p className="text-[10px] text-gray-500">Autoriser les nouveaux à s'inscrire</p></div>
                <Toggle checked={s.enable_public_registration} onChange={v => set('enable_public_registration', v)}/>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div><p className="font-semibold text-gray-900 text-sm">Mode maintenance</p><p className="text-[10px] text-gray-500">Désactiver temporairement l'app</p></div>
                <Toggle checked={s.maintenance_mode} onChange={v => set('maintenance_mode', v)} color="red"/>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div><p className="font-semibold text-gray-900 text-sm">Notifications push</p><p className="text-[10px] text-gray-500">Activer les notifications système</p></div>
                <Toggle checked={s.enable_notifications} onChange={v => set('enable_notifications', v)}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1"><Clock className="inline mr-1" size={12}/>Session (min)</label>
                  <input type="number" value={s.session_timeout} onChange={e => set('session_timeout', parseInt(e.target.value)||30)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="setting-session"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1"><HardDrive className="inline mr-1" size={12}/>Upload max (Mo)</label>
                  <input type="number" value={s.max_upload_size} onChange={e => set('max_upload_size', parseInt(e.target.value)||10)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm"/>
                </div>
              </div>
            </div>
          </div>

          {/* SMTP */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5"><Server className="text-orange-600" size={20}/><h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Serveur SMTP</h2></div>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-bold text-gray-500 block mb-1">Hôte SMTP</label>
                  <input value={s.smtp_host} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.gmail.com" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="smtp-host"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1">Port</label>
                  <input type="number" value={s.smtp_port} onChange={e => set('smtp_port', parseInt(e.target.value)||587)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="smtp-port"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1">Utilisateur SMTP</label>
                  <input value={s.smtp_user} onChange={e => set('smtp_user', e.target.value)} placeholder="user@gmail.com" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1"><Lock className="inline mr-1" size={12}/>Mot de passe</label>
                  <input type="password" value={s.smtp_password} onChange={e => set('smtp_password', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1">Email expéditeur</label>
                  <input value={s.smtp_from_email} onChange={e => set('smtp_from_email', e.target.value)} placeholder="noreply@sfoalc.fr" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1">Nom expéditeur</label>
                  <input value={s.smtp_from_name} onChange={e => set('smtp_from_name', e.target.value)} placeholder="SFOALC" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Chiffrement</label>
                <select value={s.smtp_encryption} onChange={e => set('smtp_encryption', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white">
                  <option value="tls">TLS (recommandé)</option>
                  <option value="ssl">SSL</option>
                  <option value="none">Aucun</option>
                </select>
              </div>
            </div>
          </div>

          {/* PWA & App */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5"><Bell className="text-blue-600" size={20}/><h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Application PWA</h2></div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1">Version de l'app</label>
                  <input value={s.app_version} onChange={e => set('app_version', e.target.value)} placeholder="1.0.0" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-mono" data-testid="app-version"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1">Installations PWA</label>
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50">
                    <span className="text-lg font-bold text-[#1e3a5f]">{s.pwa_install_count || 0}</span>
                    <span className="text-xs text-gray-500">installations</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Icône de l'application (512x512px)</label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {s.app_icon_url ? <img src={`${API}${s.app_icon_url}`} alt="" className="w-full h-full object-contain"/> : <Settings size={24} className="text-gray-300"/>}
                  </div>
                  <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 transition-colors flex-1">
                    <Upload size={16} className="text-gray-400"/>
                    <span className="text-sm text-gray-500">Uploader l'icône (PNG 512x512)</span>
                    <input type="file" accept="image/png" className="hidden" onChange={handleIconUpload} data-testid="app-icon-upload"/>
                  </label>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-2">Changelog</label>
                <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                  {(s.changelog || []).map((log, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-[#1e3a5f] bg-blue-50 px-2 py-0.5 rounded">v{log.version}</span>
                        <span className="text-xs text-gray-700">{log.description}</span>
                      </div>
                      <button onClick={() => removeChangelog(i)} className="text-xs text-red-400 hover:text-red-600 px-1">x</button>
                    </div>
                  ))}
                  {(!s.changelog || s.changelog.length === 0) && <p className="text-xs text-gray-400 italic">Aucun changelog</p>}
                </div>
                <div className="flex gap-2">
                  <input value={newLog.version} onChange={e => setNewLog(p => ({...p, version: e.target.value}))} placeholder="1.1.0" className="w-24 px-2 py-1.5 rounded-lg border border-gray-200 text-xs font-mono"/>
                  <input value={newLog.description} onChange={e => setNewLog(p => ({...p, description: e.target.value}))} placeholder="Description..." className="flex-1 px-2 py-1.5 rounded-lg border border-gray-200 text-xs"/>
                  <button onClick={addChangelog} className="px-3 py-1.5 bg-[#1e3a5f] text-white text-xs rounded-lg font-semibold hover:bg-[#2a4a6f]" data-testid="add-changelog">+ Ajouter</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
